import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PaymentInvoiceDTO } from '@lkp/parking-sdk';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-invoice-form-fields',
    templateUrl: './invoice-form-fields.component.html',
    styleUrls: ['./invoice-form-fields.component.scss'],
    standalone: false
})
export class InvoiceFormFieldsComponent implements OnDestroy {
  @Input() set value(invoice: PaymentInvoiceDTO | undefined) {
    if (!invoice || !this.form) return;

    this.form.patchValue(
      {
        ...invoice,
        zip: invoice.zip != null ? String(invoice.zip) : undefined,
      },
      { emitEvent: false }
    );

    // Sync checkbox + email field with existing invoice data (edit mode)
    const checked = !!invoice.invoiceEmail;
    this.eInvoiceCheck.setValue(checked, { emitEvent: false });

    checked
      ? this.form.get('invoiceEmail')?.enable({ emitEvent: false })
      : this.form.get('invoiceEmail')?.disable({ emitEvent: false });

    this.valid.emit(this.form.valid);
  }

  @Output() saved = new EventEmitter<PaymentInvoiceDTO>();
  @Output() valid = new EventEmitter<boolean>();

  readonly onDestroy$ = new Subject<void>();

  form?: FormGroup;
  eInvoiceCheck = this.fb.control(false);

  constructor(private fb: FormBuilder) {
    this.buildForm();
    this.form?.get('invoiceEmail')?.disable({ emitEvent: false });
    this.setEmailValidation();
    this.valid.emit(this.form?.valid ?? false);
  }

  private buildForm() {
    this.form = this.fb.group({
      organizationNumber: this.fb.control<string | undefined>(undefined, [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(12),
      ]),
      reference: this.fb.control<string | undefined>(undefined),
      name: this.fb.control<string | undefined>(undefined, Validators.required),
      email: this.fb.control<string | undefined>(undefined, [
        Validators.required,
        Validators.email,
      ]),
      phone: this.fb.control<string | undefined>(undefined, [
        Validators.required,
        Validators.minLength(7),
      ]),
      invoiceEmail: this.fb.control<string | undefined>(undefined, [
        Validators.email,
      ]),
      address: this.fb.control<string | undefined>(
        undefined,
        Validators.required
      ),
      zip: this.fb.control<string | undefined>(undefined, Validators.required),
      city: this.fb.control<string | undefined>(undefined, Validators.required),
      country: this.fb.control<string | undefined>(
        'Sverige',
        Validators.required
      ),
    });
  }

  private setEmailValidation() {
    // Make invoiceEmail required only when e-invoice is enabled
    this.eInvoiceCheck.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isChecked) => {
        const invoiceEmail = this.form?.get('invoiceEmail');

        invoiceEmail?.setValidators(
          isChecked
            ? [Validators.required, Validators.email] // required if checked
            : [Validators.email] // optional if not checked
        );

        // Enable or disable input based on checkbox
        isChecked
          ? invoiceEmail?.enable({ emitEvent: false })
          : invoiceEmail?.disable({ emitEvent: false });
      });

    this.form?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.valid.emit(this.form?.valid ?? false));
  }

  get invoiceEmailRequired(): boolean {
    return !!this.eInvoiceCheck.value;
  }

  save() {
    if (!this.form || !this.form.valid) {
      this.form?.markAllAsTouched();
      this.valid.emit(this.form?.valid ?? false);
      return;
    }

    const controls = this.form.controls;

    // Only include invoiceEmail if checkbox is checked
    const invoiceEmail = this.eInvoiceCheck.value
      ? controls['invoiceEmail'].value
      : undefined;

    const organizationNumberRaw = controls['organizationNumber'].value;
    const sanitizedOrganizationNumber = organizationNumberRaw
      ? organizationNumberRaw.replace('-', '')
      : undefined;

    const zipRaw = (controls['zip'].value ?? '').replace(/\s+/g, '').trim();
    const zipNumber = zipRaw ? +zipRaw : undefined;

    const invoice: PaymentInvoiceDTO = {
      organizationNumber: sanitizedOrganizationNumber,
      reference: controls['reference'].value ?? undefined,
      name: controls['name'].value ?? undefined,
      email: controls['email'].value ?? undefined,
      phone: controls['phone'].value ?? undefined,
      invoiceEmail: invoiceEmail,
      address: controls['address'].value ?? undefined,
      zip: zipNumber,
      city: controls['city'].value ?? undefined,
      country: controls['country'].value ?? undefined,
    };

    // Send cleaned, typed data up to the parent
    this.saved.emit(invoice);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
